import { LitElement, css, html, svg, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { iconPath } from './panel-icon.ts';

/**
 * `<push-button>` — illuminated soft-rubber broadcast push-button.
 *
 * The OCP-400 (and most Grass Valley / Sony control panels) carry
 * translucent polycarbonate keycaps with engraved dark legends. The
 * caps appear off-white when unlit; behind them sits a coloured LED
 * (or three) that bleeds the keycap material in a soft glow when the
 * function is available and in a saturated glow when the function is
 * actively selected.
 *
 * The engraved legend stays DARK throughout — it is engraved into the
 * keycap material, never painted over. The lit colour bleeds around
 * the legend's edges; it never overlays the text.
 *
 * State model — four physical states, exposed via the `state` attribute:
 *
 *   - `off`         the panel is powered down; the cap is its rest
 *                   off-white plastic colour, the engraving reads as
 *                   dark text against pale plastic.
 *   - `default`     the panel is powered, the function is available
 *                   but not selected; the cap glows softly in the
 *                   button's lit colour (default green).
 *   - `selected`    the function is actively selected/engaged; the
 *                   cap glows brightly in the lit colour.
 *   - `error`       an exception state (e.g. a failed white-balance
 *                   measurement) — the cap glows amber/orange to
 *                   draw the operator's attention.
 *
 * The lit colour for `default` and `selected` is driven by the CSS
 * variable `--pb-led` (typically saturated green on the OCP-400, blue
 * on PREVIEW). The `error` colour is fixed amber/orange — the same
 * shade across the Grass Valley fleet.
 */
@customElement('push-button')
export class PushButtonElement extends LitElement {
  static override styles = [
    css`
      :host {
        display: inline-block;
        contain: layout paint;
        block-size: 100%;
        inline-size: 100%;

        --pb-shell-off: var(--button-shell-off, #d8d4c8);
        --pb-shell-off-edge: var(--button-shell-off-edge, color-mix(in oklab, var(--pb-shell-off) 70%, black));
        --pb-engrave: var(--button-engrave, #18181a);
        --pb-led: var(--encoder-led-colour, #39e639);
        --pb-error: var(--button-error, #ff9a3c);
        /* Silkscreen labels on the matte black chassis read as
         * near-white. Override the token-derived warm cream with
         * a high-contrast cool white so the legends remain legible
         * against the dark face. */
        --pb-label-chassis: var(--button-label-chassis, #e8e6e2);
      }

      /* When label-position="below", the host bbox stays the cap
       * area (matching the vendor SVG button-rect dimensions) and
       * the silkscreen legend band overflows downward onto the
       * chassis. This preserves the SVG-canonical cap sizing while
       * placing the label exactly where the chassis silkscreen
       * sits — directly beneath the cap on the panel face. */
      :host([label-position='below']) {
        position: relative;
        overflow: visible;
      }

      .shell {
        display: grid;
        place-items: center;
        block-size: 100%;
        inline-size: 100%;
        border-radius: 14%;
        background:
          radial-gradient(
            ellipse at 50% 35%,
            color-mix(in oklab, var(--pb-shell-off) 92%, white) 0%,
            var(--pb-shell-off) 60%,
            color-mix(in oklab, var(--pb-shell-off) 70%, black) 100%
          );
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--pb-shell-off) 80%, white),
          inset 0 -1px 0 color-mix(in oklab, var(--pb-shell-off) 60%, black),
          0 1px 0 #000;
        font-family: var(--font-ui);
        font-size: clamp(0.28rem, 1.5cqi, 0.7rem);
        line-height: 1.05;
        font-weight: 700;
        /* Modest tracking — 0.02em gives broadcast-engraved character
         * separation without pushing the visible glyph run past the
         * narrower buttons' usable width. The matching text-indent
         * cancels the trailing letter-spacing that would otherwise
         * shift the centred line a hair to the left. */
        letter-spacing: 0.02em;
        text-indent: 0.02em;
        text-transform: uppercase;
        text-align: center;
        white-space: normal;
        word-break: normal;
        overflow-wrap: break-word;
        hyphens: none;
        padding: 0.1rem 0.08rem;
        box-sizing: border-box;
        color: var(--pb-engrave);
        cursor: pointer;
        transition:
          transform 80ms ease-out,
          background 120ms ease-out,
          box-shadow 120ms ease-out;
        user-select: none;
        container-type: inline-size;
      }

      :host([state='default']) .shell {
        background:
          radial-gradient(
            ellipse at 50% 35%,
            color-mix(in oklab, var(--pb-led) 28%, var(--pb-shell-off)) 0%,
            color-mix(in oklab, var(--pb-led) 22%, var(--pb-shell-off)) 60%,
            color-mix(in oklab, var(--pb-led) 30%, black) 100%
          );
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--pb-led) 40%, white),
          inset 0 -1px 0 color-mix(in oklab, var(--pb-led) 50%, black),
          0 0 0.3rem color-mix(in oklab, var(--pb-led) 35%, transparent),
          0 1px 0 #000;
      }

      :host([state='selected']) .shell {
        background:
          radial-gradient(
            ellipse at 50% 35%,
            color-mix(in oklab, var(--pb-led) 88%, white) 0%,
            var(--pb-led) 55%,
            color-mix(in oklab, var(--pb-led) 60%, black) 100%
          );
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--pb-led) 60%, white),
          inset 0 -1px 0 color-mix(in oklab, var(--pb-led) 60%, black),
          0 0 0.6rem color-mix(in oklab, var(--pb-led) 70%, transparent 30%),
          0 1px 0 #000;
      }

      :host([state='error']) .shell {
        background:
          radial-gradient(
            ellipse at 50% 35%,
            color-mix(in oklab, var(--pb-error) 80%, white) 0%,
            var(--pb-error) 55%,
            color-mix(in oklab, var(--pb-error) 60%, black) 100%
          );
        box-shadow:
          inset 0 1px 0 color-mix(in oklab, var(--pb-error) 60%, white),
          inset 0 -1px 0 color-mix(in oklab, var(--pb-error) 60%, black),
          0 0 0.6rem color-mix(in oklab, var(--pb-error) 70%, transparent 30%),
          0 1px 0 #000;
      }

      :host([pressed]) .shell {
        transform: translateY(1px) scale(0.96);
        box-shadow:
          inset 0 1px 1px rgba(0, 0, 0, 0.35),
          inset 0 -1px 0 rgba(255, 255, 255, 0.04),
          0 0 0.4rem color-mix(in oklab, var(--pb-led) 45%, transparent);
        filter: brightness(1.15) saturate(1.1);
      }

      .shell {
        will-change: transform, filter, box-shadow;
      }

      /* Engraved icon glyph. Real broadcast keycap engravings are
       * line drawings — paint screened or laser-etched outlines, not
       * solid filled shapes. Render the glyph as stroke-only so
       * closed sub-paths (folder body, padlock body, cursor arrows)
       * read as outlines rather than filled blocks. The few icons
       * that semantically need a solid (the centre dot of the
       * "light" sun, a triangle play arrow) carry their own fill
       * via inline attributes inside the path catalogue. */
      svg.icon {
        display: block;
        inline-size: 62%;
        block-size: 62%;
        fill: none;
        stroke: var(--pb-engrave);
        stroke-width: 1.7;
        stroke-linecap: round;
        stroke-linejoin: round;
        opacity: 0.92;
        pointer-events: none;
        vector-effect: non-scaling-stroke;
      }

      /* On a tightly stacked icon row the cap shrinks; the safer
       * minimum is to let the icon scale relative to the cap rather
       * than a fixed rem, so it stays legible at thumbnail sizes. */
      :host([dense]) svg.icon {
        inline-size: 70%;
        block-size: 70%;
        stroke-width: 1.9;
      }

      /* Below-cap silkscreen label band. Positioned absolutely
       * directly beneath the cap shell so the cap can fill the
       * SVG-canonical bbox without sharing space with the label.
       * The label reads as moulded silkscreen text on the chassis
       * face. white-space pre-line respects newlines in the label
       * value so multi-line legends like SKIN newline DETAIL or
       * BLACK newline GAMMA stack as the physical panel does. */
      .below-label {
        position: absolute;
        inset-block-start: 105%;
        inset-inline-start: -20%;
        inline-size: 140%;
        text-align: center;
        font-family: var(--font-ui);
        /* Min 0.5rem keeps the label readable at thumbnail panel
         * scales; cqi-based scaling makes it grow proportionally
         * up to a 0.95rem cap so a fullscreen panel does not push
         * the label into the next control row. */
        font-size: clamp(0.5rem, 5cqi, 0.95rem);
        font-weight: 600;
        letter-spacing: 0.03em;
        text-transform: uppercase;
        color: var(--pb-label-chassis);
        line-height: 1.0;
        white-space: pre-line;
        pointer-events: none;
        user-select: none;
        /* Silkscreen on the matte chassis surface — slight glow so
         * the label reads as moulded white-ink screening rather
         * than as a thin solid white pixel run. */
        text-shadow: 0 0 0.05rem rgba(0, 0, 0, 0.55);
      }
    `,
  ];

  /** Press behaviour. */
  @property({ type: String, reflect: true }) accessor mode:
    | 'momentary'
    | 'latching'
    | 'exclusive' = 'momentary';

  /**
   * Physical illumination state.
   *
   *  - `off`      — panel down or no power
   *  - `default`  — panel on, function available
   *  - `selected` — function actively engaged
   *  - `error`    — exception (e.g. white-balance measurement failed)
   */
  @property({ type: String, reflect: true }) accessor state:
    | 'off'
    | 'default'
    | 'selected'
    | 'error' = 'off';

  /** Whether the button is in a visually depressed state. */
  @property({ type: Boolean, reflect: true }) accessor pressed = false;

  /** Engraved legend text. */
  @property({ type: String }) accessor label = '';

  /**
   * Engraved icon glyph name. When set, the cap renders the matching
   * SVG path from `panel-icon`'s catalogue instead of the text label.
   * Unknown names fall back to the text label so a typo does not
   * silently produce a blank cap.
   */
  @property({ type: String }) accessor icon = '';

  /**
   * Where the engraved legend sits relative to the button cap.
   *
   *   - `on` (default) — legend silkscreened across the cap's centre.
   *     Used by Grass Valley OCP-400 / CGP-500 and most broadcast
   *     panels that print the button's function name on the keycap.
   *   - `below` — legend silkscreened on the CHASSIS underneath the
   *     cap. The Sony fleet (RCP-3500, RCP-1500 lineage) uses small
   *     unlabelled coloured keycaps with their function printed on
   *     the panel face directly beneath each one.
   *
   * The cap geometry is unchanged in both modes; only the legend's
   * position differs. The host element's bounding box widens
   * downward in `below` mode to accommodate the label band, but the
   * shell stays anchored to the top of the bbox so the button's
   * physical mounting point on the chassis is unaffected.
   */
  @property({ type: String, reflect: true, attribute: 'label-position' })
  accessor labelPosition: 'on' | 'below' = 'on';

  /**
   * Compact-icon mode for tightly stacked rows (the CGP-500 CGA-
   * shortcut row sits seven icons across in roughly 50 mm of chassis
   * width; the icon needs more cap real estate to remain readable at
   * panel scale). The CSS reads this attribute via `:host([dense])`.
   */
  @property({ type: Boolean, reflect: true }) accessor dense = false;

  #activePointerId = -1;

  #onPointerDown = (event: PointerEvent) => {
    if (event.button !== 0) return;
    this.#activePointerId = event.pointerId;
    this.pressed = true;
    (event.currentTarget as Element).setPointerCapture(event.pointerId);
    if (this.mode === 'latching' || this.mode === 'exclusive') {
      this.state = this.state === 'selected' ? 'default' : 'selected';
    }
    this.dispatchEvent(new CustomEvent('button-press', {
      bubbles: true,
      composed: true,
      detail: { label: this.label, mode: this.mode, state: this.state },
    }));
    event.preventDefault();
  };

  #onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== this.#activePointerId) return;
    this.#activePointerId = -1;
    if (this.mode === 'momentary') {
      this.pressed = false;
    } else {
      // Latching / exclusive: stay visually pressed only while selected.
      this.pressed = this.state === 'selected';
    }
    const target = event.currentTarget as Element;
    if (target.hasPointerCapture(event.pointerId)) {
      target.releasePointerCapture(event.pointerId);
    }
    this.dispatchEvent(new CustomEvent('button-release', {
      bubbles: true,
      composed: true,
      detail: { label: this.label, mode: this.mode, state: this.state },
    }));
  };

  override render() {
    const glyph = iconPath(this.icon);
    // The "light" sun and the play-triangle arrows need a solid
    // centre. Everything else renders as stroke-only — matching the
    // real engraved-outline character of broadcast keycaps.
    const filled = this.icon === 'light' ||
      this.icon === 'prev' ||
      this.icon === 'next' ||
      this.icon === 'up' ||
      this.icon === 'down' ||
      this.icon === 'left' ||
      this.icon === 'right';
    // On-cap content: icon if present, otherwise the text label. In
    // label-position="below" mode the cap stays blank and the label
    // renders in a separate band beneath the shell.
    const onCapContent = glyph
      ? svg`
          <svg class="icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d=${glyph} fill=${filled ? 'var(--pb-engrave)' : 'none'} />
          </svg>
        `
      : this.labelPosition === 'below'
        ? nothing
        : (this.label || nothing);
    return html`
      <div
        class="shell"
        role="button"
        aria-pressed=${this.state === 'selected'}
        aria-label=${this.icon || this.label || nothing}
        @pointerdown=${this.#onPointerDown}
        @pointerup=${this.#onPointerUp}
        @pointercancel=${this.#onPointerUp}
      >
        ${onCapContent}
      </div>
      ${this.labelPosition === 'below' && this.label
        ? html`<div class="below-label">${this.label}</div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'push-button': PushButtonElement;
  }
}
